/* eslint-disable no-console */
import { debounce } from '@navi/web-ui/lib/utils';
import React, { useState, useEffect, useRef, CSSProperties } from 'react';
import { ThresholdUnits, parseThreshold } from './threshold';

type Fn = () => any;
export interface Props {
  next: Fn;
  hasMore: boolean;
  children: React.ReactNode;
  loader: React.ReactNode;
  scrollThreshold?: number | string;
  endMessage?: React.ReactNode;
  style?: React.CSSProperties;
  height?: number | string;
  maxHeight?: number | string;
  scrollableTarget?: React.ReactNode;
  hasChildren?: boolean;
  inverse?: boolean;
  refreshFunction?: Fn;
  onScroll?: (e: MouseEvent) => any;
  dataLength: number;
  initialScrollY?: number;
  outerContainerclassName?: string;
  inputString?: string;
  currentPage?: string | null;
}

const InfiniteScroll: React.FC<Props> = ({
  next,
  hasMore,
  children,
  loader,
  scrollThreshold = 0.8,
  endMessage,
  style,
  height,
  maxHeight,
  scrollableTarget,
  hasChildren,
  inverse,
  refreshFunction,
  onScroll,
  dataLength,
  initialScrollY,
  outerContainerclassName = '',
  inputString = '',
  currentPage = null
}) => {
  const [showLoader, setShowLoader] = useState(false);
  const [pullToRefreshThresholdBreached, setPullToRefreshThresholdBreached] = useState(false);
  const [prevDataLength, setPrevDataLength] = useState<number>(0);

  const throttledOnScrollListener = (e: MouseEvent) => {
    debounce(() => {
      onScrollListener(e);
    }, 0)();
  };
  const scrollableNodeRef = useRef<HTMLElement | undefined | null>(null);
  const elRef = useRef<HTMLElement | (Window & typeof globalThis) | undefined>(undefined);
  const infScrollRef = useRef<HTMLDivElement | undefined>(undefined);
  const pullDownRef = useRef<HTMLDivElement | undefined>(undefined);

  const lastScrollTopRef = useRef(0);
  const actionTriggeredRef = useRef(false);

  // variables to keep track of pull down behaviour
  const startYRef = useRef<number>(0);
  const currentYRef = useRef<number>(0);
  const draggingRef = useRef<boolean>(false);

  // will be populated in useEffect
  // based on the height of the pull down element
  const maxPullDownDistance = useRef<number>(0);

  const styles = {
    height: height || 'auto',
    maxHeight: maxHeight || 'auto',
    overflow: 'auto',
    WebkitOverflowScrolling: 'touch',
    ...style
  } as CSSProperties;

  const getScrollableTarget = () => {
    if (scrollableTarget instanceof HTMLElement) return scrollableTarget;
    if (typeof scrollableTarget === 'string') {
      return document.getElementById(scrollableTarget);
    }
    return null;
  };

  const onStart = (evt: Event) => {
    if (lastScrollTopRef.current) return;

    draggingRef.current = true;

    if (evt instanceof MouseEvent) {
      startYRef.current = evt.pageY;
    } else if (evt instanceof TouchEvent) {
      startYRef.current = evt.touches[0].pageY;
    }
    currentYRef.current = startYRef.current;

    if (infScrollRef.current) {
      infScrollRef.current.style.willChange = 'transform';
      infScrollRef.current.style.transition = `transform 0.2s cubic-bezier(0,0,0.31,1)`;
    }
  };

  const onMove = (evt: Event) => {
    if (!draggingRef.current) return;

    if (evt instanceof MouseEvent) {
      currentYRef.current = evt.pageY;
    } else if (evt instanceof TouchEvent) {
      currentYRef.current = evt.touches[0].pageY;
    }

    // user is scrolling down to up
    if (currentYRef.current < startYRef.current) return;

    // so you can drag upto 1.5 times of the maxPullDownDistance
    if (currentYRef.current - startYRef.current > maxPullDownDistance.current * 1.5) return;

    if (infScrollRef.current) {
      infScrollRef.current.style.overflow = 'visible';
      infScrollRef.current.style.transform = `translate3d(0px, ${
        currentYRef.current - startYRef.current
      }px, 0px)`;
    }
  };

  const onEnd = () => {
    startYRef.current = 0;
    currentYRef.current = 0;

    draggingRef.current = false;

    if (pullToRefreshThresholdBreached) {
      refreshFunction && refreshFunction();
      setPullToRefreshThresholdBreached(false);
    }

    requestAnimationFrame(() => {
      if (infScrollRef.current) {
        infScrollRef.current.style.overflow = 'auto';
        infScrollRef.current.style.transform = 'none';
        infScrollRef.current.style.willChange = 'unset';
      }
    });
  };

  const isElementAtTop = (target: HTMLElement, scrollThreshold: string | number = 0.8) => {
    const clientHeight =
      target === document.body || target === document.documentElement
        ? window.screen.availHeight
        : target.clientHeight;

    const threshold = parseThreshold(scrollThreshold);

    if (threshold.unit === ThresholdUnits.Pixel) {
      return target.scrollTop <= threshold.value + clientHeight - target.scrollHeight + 1;
    }

    return target.scrollTop <= threshold.value / 100 + clientHeight - target.scrollHeight + 1;
  };

  const isElementAtBottom = (target: HTMLElement, scrollThreshold: string | number = 0.8) => {
    const clientHeight =
      target === document.body || target === document.documentElement
        ? window.screen.availHeight
        : target.clientHeight;

    const threshold = parseThreshold(scrollThreshold);

    if (threshold.unit === ThresholdUnits.Pixel) {
      return target.scrollTop + clientHeight >= target.scrollHeight - threshold.value;
    }

    return target.scrollTop + clientHeight >= (threshold.value / 100) * target.scrollHeight;
  };

  const onScrollListener = (event: MouseEvent) => {
    if (typeof onScroll === 'function') {
      // Execute this callback in next tick so that it does not affect the
      // functionality of the library.
      setTimeout(() => onScroll && onScroll(event), 0);
    }

    const target =
      maxHeight || scrollableNodeRef
        ? (event.target as HTMLElement)
        : document.documentElement.scrollTop
        ? document.documentElement
        : document.body;

    // return immediately if the action has already been triggered,
    // prevents multiple triggers.
    if (actionTriggeredRef.current) return;

    const atBottom = inverse
      ? isElementAtTop(target, scrollThreshold)
      : isElementAtBottom(target, scrollThreshold);

    // call the `next` function in the to trigger the next data fetch
    if (atBottom && hasMore) {
      actionTriggeredRef.current = true;
      setShowLoader(true);
      if (next) {
        next();
      }
    }

    lastScrollTopRef.current = target.scrollTop;
  };

  useEffect(() => {
    if (typeof dataLength === 'undefined') {
      throw new Error(
        `mandatory prop "dataLength" is missing. The prop is needed` +
          ` when loading more content. Check README.md for usage`
      );
    }

    scrollableNodeRef.current = getScrollableTarget();
    elRef.current = maxHeight ? infScrollRef.current : scrollableNodeRef.current || window;

    if (elRef.current) {
      elRef.current.addEventListener(
        'scroll',
        throttledOnScrollListener as EventListenerOrEventListenerObject
      );
    }

    if (
      typeof initialScrollY === 'number' &&
      elRef.current &&
      elRef.current instanceof HTMLElement &&
      elRef.current.scrollHeight > initialScrollY
    ) {
      elRef.current.scrollTo(0, initialScrollY);
    }

    return () => {
      if (elRef.current) {
        elRef.current.removeEventListener(
          'scroll',
          throttledOnScrollListener as EventListenerOrEventListenerObject
        );
      }
    };
  }, []);

  useEffect(() => {
    if (dataLength !== prevDataLength) {
      actionTriggeredRef.current = false;
      setPrevDataLength(dataLength);
      setShowLoader(false);
    }
  }, [dataLength]);

  return (
    <div className={outerContainerclassName}>
      <div
        className={`infinite-scroll-component`}
        ref={(infScroll: HTMLDivElement) => (infScrollRef.current = infScroll)}
        style={styles}
      >
        {children}
        {!showLoader && !hasChildren && hasMore && loader}
        {showLoader && hasMore && loader}
        {!hasMore && endMessage}
      </div>
    </div>
  );
};

export default InfiniteScroll;
